import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { freeCheckInputSchema, freeCheckStatsInputSchema } from "./free-check.schemas";

export type FreeCheckStatus = "ok" | "invalid_domain" | "ip_limited" | "ceiling_reached" | "bot_blocked" | "error";

export type FreeCheckStage = {
  pct: number;
  named: number;
  total: number;
  /** Rival most often named in this stage's sampled answers, if any. */
  topCompetitor?: { name: string; count: number };
};

export type FreeCheckResult = {
  status: FreeCheckStatus;
  domain?: string;
  brand?: string;
  category?: string;
  named_pct?: number;
  own_named?: boolean;
  perStage?: {
    discovery: FreeCheckStage;
    evaluation: FreeCheckStage;
    decision: FreeCheckStage;
  };
  prompts?: string[];
  discoveryText?: string;
  /** First 1-2 sentences of a later-stage answer, used for the partial reveal. */
  evaluationTeaser?: string;
  competitors?: string[];
  /** Whether extracted competitors passed the same-category relevance filter. */
  competitorConfidence?: "verified" | "loose";
  /** Version of the public report contract used to produce this result. */
  resultVersion?: number;
  sentence?: string;
  cache_hit?: boolean;
  generated_at?: string;
  message?: string;
};


export const runFreeVisibilityCheck = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => freeCheckInputSchema.parse(input))
  .handler(async ({ data }): Promise<FreeCheckResult> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Campaign attribution: stored alongside every check row so placements
    // (blog posts, directories, outreach) are distinguishable in the funnel.
    const attribution = {
      utm_source: data.utm_source || null,
      utm_medium: data.utm_medium || null,
      utm_campaign: data.utm_campaign || null,
      referrer: data.referrer || null,
    };
    const {
      normalizeDomain,
      hashIp,
      localDay,
      runLightweightScan,
      buildStageFinding,
      FREE_CHECK_CONFIG,
    } = await import("./free-check.server");

    const domain = normalizeDomain(data.domain);
    if (!domain) return { status: "invalid_domain", message: "Enter a domain like yourcompany.com" };

    const ip =
      getRequestHeader("cf-connecting-ip") ||
      getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim() ||
      getRequestHeader("x-real-ip") ||
      null;
    // Abuse control is server-side only: per-IP rate limit, daily ceiling, and
    // the 48h domain cache. No third-party bot widget is loaded in the browser.

    const ipHash = hashIp(ip);
    const day = localDay();

    const { isAllowlistedIp } = await import("./ip-allowlist");
    const bypass = isAllowlistedIp(ip);

    // 1) Domain cache — served to everyone within window (allowlisted IPs skip cache).
    const cacheCutoff = new Date(Date.now() - FREE_CHECK_CONFIG.cacheDays * 86400_000).toISOString();
    const cached = bypass ? { data: null } : await supabaseAdmin
      .from("free_visibility_checks")
      .select("result, created_at")
      .eq("domain", domain)
      .eq("cache_hit", false)
      .eq("status", "ok")
      .gte("created_at", cacheCutoff)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();


    if (cached.data?.result) {
      const r: any = cached.data.result;
      // Only serve cache when it has the new per-stage shape; otherwise fall through
      // to a fresh scan so we don't render an incomplete result.
      const hasCurrentPrompts = Array.isArray(r.prompts) && r.prompts.length > 0 && r.prompts.every(
        (prompt: unknown) => typeof prompt === "string" && !prompt.includes("primary product") && !prompt.includes("for example:"),
      );
      const currentResult =
        r.resultVersion === FREE_CHECK_CONFIG.resultVersion &&
        (r.competitorConfidence === "verified" || r.competitorConfidence === "loose") &&
        hasCurrentPrompts;
      if (currentResult && r.perStage?.discovery && r.perStage?.evaluation && r.perStage?.decision) {
        await supabaseAdmin.from("free_visibility_checks").insert({
          domain,
          ip_hash: ipHash,
          result: r,
          cache_hit: true,
          cost_usd: 0,
          status: "ok",
          local_day: day,
          ...attribution,
        });
        return {
          status: "ok",
          domain,
          brand: r.brand,
          category: r.category,
          named_pct: r.named_pct,
          own_named: r.own_named ?? ((r.named_pct ?? 0) > 0),
          perStage: r.perStage,
          prompts: r.prompts,
          discoveryText: r.discoveryText,
          evaluationTeaser: r.evaluationTeaser,
          competitors: r.competitors ?? [],
          competitorConfidence: r.competitorConfidence === "verified" ? "verified" : "loose",
          resultVersion: r.resultVersion,
          sentence: r.sentence,
          cache_hit: true,
          generated_at: cached.data.created_at,
        };
      }
    }

    // 2) Per-IP daily cap on fresh scans, shared across every public scan tool
    //    (/check, /reddit-check, instant check) plus a burst cooldown.
    const allowlisted = bypass;
    if (!allowlisted) {
      const { checkPublicScanLimit } = await import("./public-scan-limit.server");
      const verdict = await checkPublicScanLimit(ipHash, day);
      if (!verdict.allowed) {
        await supabaseAdmin.from("free_visibility_checks").insert({
          domain, ip_hash: ipHash, cache_hit: false, cost_usd: 0, status: "ip_limited", local_day: day, ...attribution,
        });
        return {
          status: "ip_limited",
          message: verdict.message,
        };
      }

      // 3) Daily ceiling across all users.
      const dayCount = await supabaseAdmin
        .from("free_visibility_checks")
        .select("id", { count: "exact", head: true })
        .eq("local_day", day)
        .eq("cache_hit", false)
        .eq("status", "ok");
      if ((dayCount.count ?? 0) >= FREE_CHECK_CONFIG.dailyCeiling) {
        await supabaseAdmin.from("free_visibility_checks").insert({
          domain, ip_hash: ipHash, cache_hit: false, cost_usd: 0, status: "ceiling_reached", local_day: day, ...attribution,
        });
        return {
          status: "ceiling_reached",
          message: "High demand right now, start your free trial to check your visibility.",
        };
      }
    }


    // 4) Shared daily spend cap for all free usage (applies to allowlisted IPs too).
    const { freeCapacityLeft, logFreeSpend, FREE_CAPACITY_MESSAGE } = await import("./free-spend.server");
    if (!(await freeCapacityLeft())) {
      await supabaseAdmin.from("free_visibility_checks").insert({
        domain, ip_hash: ipHash, cache_hit: false, cost_usd: 0, status: "ceiling_reached", local_day: day, ...attribution,
      });
      return { status: "ceiling_reached", message: FREE_CAPACITY_MESSAGE };
    }

    // 5) Run the scan.
    const scanStarted = Date.now();
    const scan = await runLightweightScan(domain);
    await logFreeSpend("free_homepage_check", scan.cost_usd, { durationMs: Date.now() - scanStarted, error: scan.error ?? null });
    if (scan.error && scan.named_pct === 0) {
      await supabaseAdmin.from("free_visibility_checks").insert({
        domain, ip_hash: ipHash, cache_hit: false, cost_usd: scan.cost_usd, status: "error", local_day: day, ...attribution,
        result: { error: scan.error },
      });
      return { status: "error", message: "We couldn't reach the AI providers just now. Try again in a minute." };
    }

    const sentence = buildStageFinding(scan.brand, scan.perStage, scan.competitors ?? []);
    const perStage = {
      discovery: { ...scan.perStage.discovery },
      evaluation: { ...scan.perStage.evaluation },
      decision: { ...scan.perStage.decision },
    };
    const result = {
      named_pct: scan.named_pct,
      own_named: scan.own_named,
      perStage,
      brand: scan.brand,
      category: scan.category,
      sentence,
      prompts: scan.prompts,
      discoveryText: scan.discoveryText,
      evaluationTeaser: scan.evaluationTeaser,
      competitors: scan.competitors,
      competitorConfidence: scan.competitorConfidence,
      resultVersion: FREE_CHECK_CONFIG.resultVersion,
      providers: ["gemini", "perplexity"],
    };
    // Fire-and-forget promotion into the prospect queue. Never blocks or
    // errors the user's response — worst case we skip promotion this round.
    let promotion: any = null;
    try {
      const { maybePromoteFreeCheck } = await import("./free-check-promote.server");
      promotion = await maybePromoteFreeCheck({
        domain,
        brand: scan.brand,
        category: scan.category,
        siteReachable: scan.siteReachable,
      });
    } catch (e) {
      promotion = { promoted: false, reason: `error:${e instanceof Error ? e.message.slice(0, 80) : String(e)}` };
    }
    const resultWithPromotion = { ...result, promotion };

    const ins = await supabaseAdmin.from("free_visibility_checks").insert({
      domain, ip_hash: ipHash, cache_hit: false, cost_usd: scan.cost_usd, status: "ok", local_day: day, result: resultWithPromotion, ...attribution,
    }).select("created_at").maybeSingle();

    return {
      status: "ok",
      domain,
      brand: scan.brand,
      category: scan.category,
      named_pct: scan.named_pct,
      own_named: scan.own_named,
      perStage,
      prompts: scan.prompts,
      discoveryText: scan.discoveryText,
      evaluationTeaser: scan.evaluationTeaser,
      competitors: scan.competitors,
      competitorConfidence: scan.competitorConfidence,
      resultVersion: FREE_CHECK_CONFIG.resultVersion,
      sentence,
      cache_hit: false,
      generated_at: ins.data?.created_at ?? new Date().toISOString(),
    };
  });



// Admin-only stats for the dashboard.
export const getFreeCheckStats = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => freeCheckStatsInputSchema.parse(input ?? {}))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const authz = getRequestHeader("authorization") || "";
    const token = authz.replace(/^Bearer\s+/i, "");
    if (!token) return { unauthorized: true as const };
    const { data: userData, error } = await supabaseAdmin.auth.getUser(token);
    if (error || !userData.user) return { unauthorized: true as const };
    const roleRes = await supabaseAdmin.rpc("has_role", { _user_id: userData.user.id, _role: "admin" });
    if (!roleRes.data) return { unauthorized: true as const };

    const { FREE_CHECK_CONFIG, localDay } = await import("./free-check.server");
    const day = localDay();
    const days = data.days ?? 7;
    const since = new Date(Date.now() - days * 86400_000).toISOString();

    const todayFresh = await supabaseAdmin.from("free_visibility_checks")
      .select("id", { count: "exact", head: true })
      .eq("local_day", day).eq("cache_hit", false).eq("status", "ok");
    const todayCached = await supabaseAdmin.from("free_visibility_checks")
      .select("id", { count: "exact", head: true })
      .eq("local_day", day).eq("cache_hit", true);
    const todaySpendRes = await supabaseAdmin.from("free_visibility_checks")
      .select("cost_usd")
      .eq("local_day", day);
    const todaySpend = (todaySpendRes.data ?? []).reduce((s, r: any) => s + Number(r.cost_usd || 0), 0);

    const recent = await supabaseAdmin.from("free_visibility_checks")
      .select("id, domain, ip_hash, cache_hit, cost_usd, status, created_at, result")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(50);

    return {
      unauthorized: false as const,
      config: FREE_CHECK_CONFIG,
      today: {
        fresh: todayFresh.count ?? 0,
        cached: todayCached.count ?? 0,
        remaining: Math.max(0, FREE_CHECK_CONFIG.dailyCeiling - (todayFresh.count ?? 0)),
        spend_usd: todaySpend,
      },
      recent: recent.data ?? [],
    };
  });
